/**
 * Combines Samsara's route timing with Track-POD's route lifecycle.
 *
 * The route-level twin of stopTiming.ts, and it follows the same asymmetric rule for the same
 * reasons: Samsara wins wherever it has an opinion, Track-POD fills silence.
 *
 * The silence here is specific. `actual_start_time` has exactly one writer, the Samsara sync,
 * which sets it from a depot geofence. When that geofence never fires the column stays null for
 * the rest of the day — and because the route's own status and completion are derived from it, a
 * route that demonstrably ran reads "Late start +12h", with a counter climbing since dawn, above
 * a list of collections the driver has already signed for.
 *
 * Track-POD holds the missing half. Its Route object carries a lifecycle the driver drives
 * himself — Draft, Ready, Loaded, InProgress, Closed — plus StartDate and CloseDate. Those are
 * not derived from anything; they are the driver pressing start and pressing close. A closed
 * route is a person stating the run is over, which is exactly the assertion a silent geofence
 * failed to make.
 *
 * That makes this resolver better placed than the stop-level one to answer "when did it start".
 * inferPassedStops deliberately refuses to synthesise a time from a stop arrival, because the
 * first stop's arrival is an arrival at the first stop and not a departure from the depot.
 * StartDate has no such problem: it is a real recorded route start, so where Samsara has none,
 * this hands over a measured value rather than leaving the field blank or making one up.
 *
 * Nothing here writes. Resolution happens at read time in toRouteLegacy, the Samsara columns keep
 * one writer, and a disagreement stays visible rather than being reconciled away.
 */
import type { RouteInstance, RouteInstanceStatus } from "@/types/collections"
import { isPlausibleWithin, toDate } from "./timingResolution"
import type { TimingSource } from "@/lib/stopTiming"

export interface ResolvedRouteTiming {
  actualStart?: Date
  actualStartSource: TimingSource
  actualEnd?: Date
  actualEndSource: TimingSource
  status: RouteInstanceStatus
  statusSource: TimingSource
  /** The two systems tell different stories about this route. */
  disagreement: boolean
}

/**
 * How far from the plan a driver-recorded route time may fall before it is discarded.
 *
 * Wider than the stop-level window, because a route's planned start is a single point that the
 * whole day drifts away from: a run that sets off two hours late and finishes six hours after its
 * planned finish is a bad day, not a bad timestamp. The values being caught are still the manual
 * entry failures — a route closed the following morning, a date picker on the wrong day — which
 * land a day or more out.
 */
export const TRACKPOD_ROUTE_PLAUSIBILITY_WINDOW_HOURS = 18

/** Track-POD route statuses meaning the driver has the route but has not set off. */
const NOT_STARTED = new Set(["Draft", "Ready", "Loaded"])

/** The driver has closed the route: the run is over. */
const CLOSED = "Closed"

/** The driver has started the route and is out on it. */
const IN_PROGRESS = "InProgress"

function isPlausible(candidate: Date, reference: Date): boolean {
  return isPlausibleWithin(candidate, reference, TRACKPOD_ROUTE_PLAUSIBILITY_WINDOW_HOURS)
}

/**
 * Resolve one route's timing and status from both systems.
 *
 * Pure, and takes only the fields it reads, so it can be tested without building a whole route.
 */
export function resolveRouteTiming(
  route: Pick<
    RouteInstance,
    | "status"
    | "actual_start_time"
    | "actual_end_time"
    | "planned_start_time"
    | "planned_end_time"
    | "trackpod_status"
    | "trackpod_start"
    | "trackpod_close"
  >
): ResolvedRouteTiming {
  const samsaraStart = toDate(route.actual_start_time)
  const samsaraEnd = toDate(route.actual_end_time)
  const plannedStart = toDate(route.planned_start_time)
  const plannedEnd = toDate(route.planned_end_time)
  const tpStatus = route.trackpod_status ?? undefined

  const rawTpStart = toDate(route.trackpod_start)
  const tpStart =
    rawTpStart && plannedStart && isPlausible(rawTpStart, plannedStart) ? rawTpStart : undefined

  // A close before the start it belongs to is self-contradictory whatever the plan says, and is
  // the commonest manual-entry error — so it is checked against the resolved start as well.
  const rawTpClose = toDate(route.trackpod_close)
  const effectiveStart = samsaraStart ?? tpStart
  const tpClose =
    rawTpClose &&
    plannedStart &&
    isPlausible(rawTpClose, plannedEnd ?? plannedStart) &&
    !(effectiveStart && rawTpClose < effectiveStart)
      ? rawTpClose
      : undefined

  const actualStart = samsaraStart ?? tpStart
  const actualStartSource: TimingSource = samsaraStart ? "samsara" : tpStart ? "trackpod" : "none"

  const actualEnd = samsaraEnd ?? tpClose
  const actualEndSource: TimingSource = samsaraEnd ? "samsara" : tpClose ? "trackpod" : "none"

  const { status, statusSource, statusDisagrees } = resolveStatus(route.status, tpStatus)

  return {
    actualStart,
    actualStartSource,
    actualEnd,
    actualEndSource,
    status,
    statusSource,
    // A Track-POD-sourced start is itself a disagreement: Samsara had nothing where the driver
    // had something, which is the geofence miss this module exists for.
    disagreement: statusDisagrees || actualStartSource === "trackpod",
  }
}

function resolveStatus(
  samsara: RouteInstanceStatus,
  tpStatus: string | undefined
): { status: RouteInstanceStatus; statusSource: TimingSource; statusDisagrees: boolean } {
  const keep = { status: samsara, statusSource: "samsara" as TimingSource, statusDisagrees: false }

  if (!tpStatus) return keep

  // A cancelled route is an office decision, not an observation, and outranks anything the
  // driver's app has to say about it — including a route he started before the cancellation
  // reached him. Completed is Samsara already agreeing; there is nothing to resolve.
  if (samsara === "cancelled" || samsara === "completed") {
    return {
      status: samsara,
      statusSource: "samsara",
      // Worth flagging: a driver out on a route the office has cancelled is a phone call.
      statusDisagrees: samsara === "cancelled" && tpStatus === IN_PROGRESS,
    }
  }

  // The target case. The driver closed the route; Samsara never saw it start, or saw it start and
  // never saw it finish. Closing is an explicit act with nothing else it could mean.
  if (tpStatus === CLOSED) {
    return { status: "completed", statusSource: "trackpod", statusDisagrees: true }
  }

  // The same miss one step earlier: the driver is out, Samsara still has the route scheduled.
  if (tpStatus === IN_PROGRESS && samsara === "scheduled") {
    return { status: "in_progress", statusSource: "trackpod", statusDisagrees: true }
  }

  // Samsara has the route moving; Track-POD says the driver never started it. Samsara keeps the
  // status — a vehicle that moved, moved — but a run happening outside the app is a real
  // operational signal, because it means no PODs are being captured.
  if (NOT_STARTED.has(tpStatus) && samsara !== "scheduled") {
    return { status: samsara, statusSource: "samsara", statusDisagrees: true }
  }

  return keep
}
